import React from 'react';
import { Check, Loader2, RefreshCw } from 'lucide-react';
import { useDashboard } from './dashboardContext';
import ThemePicker from '../ThemePicker';
import IdentityCard from './IdentityCard';
import MoreSection from './MoreSection';
import SettingsPreview from './SettingsPreview';

// Central "Iestatījumi" page: everything that configures how the business page
// looks and who it is — identity (name/logo/address/socials), the public-page
// colour motif, and the owner dashboard panel motif — in one place.
export default function SettingsSection() {
  const { activeTab, section, cfg, mc, rc, bc, setMc, setRc, setBc,
    save, saving, panelSurface, panelAccent, savePanelTheme, regeneratePage, regenBusy } = useDashboard();

  if (activeTab !== 'manage' || section !== 'settings' || !cfg?.template) return null;

  const tpl = cfg.template;
  const cfgObj = tpl === 'menu' ? mc : tpl === 'business_card' ? bc : rc;
  const surface = cfgObj.surface;
  const accent = cfgObj.accent;
  const setSurface = (id) => (tpl === 'menu' ? setMc({ surface: id }) : tpl === 'business_card' ? setBc({ surface: id }) : setRc({ surface: id }));
  const setAccent = (id) => (tpl === 'menu' ? setMc({ accent: id }) : tpl === 'business_card' ? setBc({ accent: id }) : setRc({ accent: id }));

  // Live-preview sample content per template.
  const name = cfg.display_name || cfg.name || '';
  const region = cfg.region || '';
  let sampleLabel = '', samplePrice = null, ctaLabel = 'Rezervēt';
  if (tpl === 'menu') {
    sampleLabel = mc.daily_special || mc.categories?.[0]?.items?.[0]?.name || 'Dienas zupa';
    samplePrice = mc.daily_special_orderable ? mc.daily_special_price : (mc.categories?.[0]?.items?.[0]?.price ?? null);
    ctaLabel = mc.daily_special_orderable ? 'Pasūtīt' : 'Rezervēt galdiņu';
  } else if (tpl === 'reservations') {
    sampleLabel = rc.services?.[0]?.name || 'Konsultācija';
    samplePrice = rc.services?.[0]?.price ?? null;
    ctaLabel = 'Rezervēt laiku';
  } else {
    sampleLabel = 'Sazinies ar mums';
    ctaLabel = 'Sazināties';
  }

  return (
    <div className="space-y-6" data-testid="settings-section">
      <p className="text-white/45 text-sm -mt-2">Identitāte, kontakti, rekvizīti, krāsas un domēns — viss vienuviet.</p>

      <div className="grid lg:grid-cols-[1fr_300px] gap-6 items-start">
        <div className="space-y-6 min-w-0 order-last lg:order-first">
          <IdentityCard />

          {cfg?.generated_from && (
            <div className="rounded-2xl border border-[var(--accent)]/30 bg-[var(--accent)]/[0.06] p-4 sm:p-5" data-testid="regenerate-card">
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-lg bg-[var(--accent)]/15 text-[var(--accent)] flex items-center justify-center shrink-0">
                  <RefreshCw className="w-4.5 h-4.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-white font-semibold text-sm" style={{ fontFamily: 'Outfit' }}>Atjaunot no avota saites</p>
                  <p className="text-white/50 text-xs mt-0.5">Pārvelk svaigāko saturu (pakalpojumus, cenas, attēlus) no <span className="text-white/70 break-all">{cfg.generated_from}</span>. Pašreizējais saturs tiks aizstāts.</p>
                </div>
              </div>
              <button onClick={regeneratePage} disabled={regenBusy}
                className="mt-3 w-full h-11 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold text-sm hover:bg-[var(--accent-hover)] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="regenerate-page-btn">
                {regenBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Atjaunojam…</> : <><RefreshCw className="w-4 h-4" /> Atjaunot no saites</>}
              </button>
            </div>
          )}

          <ThemePicker
            title="Publiskās lapas motīvs"
            subtitle="Fons un akcenta krāsa, kas redzami tavā publiskajā uzņēmuma lapā. Priekšskatījums atjaunojas uzreiz."
            surface={surface}
            accent={accent}
            template={tpl}
            onChangeSurface={setSurface}
            onChangeAccent={setAccent}
          />

          <ThemePicker
            families={['dark']}
            title="Vadības paneļa motīvs"
            subtitle="Tava vadības paneļa fons (tumšie toņi) un akcenta krāsa. Neietekmē publisko lapu."
            surface={panelSurface}
            accent={panelAccent}
            template="reservations"
            onChangeSurface={(id) => savePanelTheme({ surface: id })}
            onChangeAccent={(id) => savePanelTheme({ accent: id })}
          />
        </div>

        <div className="order-first lg:order-last lg:sticky lg:top-4">
          <SettingsPreview
            surface={surface}
            accent={accent}
            template={tpl}
            name={name}
            tagline={cfgObj.tagline}
            coverUrl={cfgObj.cover_url}
            region={region}
            sampleLabel={sampleLabel}
            samplePrice={samplePrice}
            ctaLabel={ctaLabel}
          />
        </div>
      </div>

      <MoreSection />

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 h-12 px-8 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] disabled:opacity-60"
          style={{ boxShadow: '0 8px 30px var(--accent-ring)' }} data-testid="settings-save-btn">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />} Saglabāt
        </button>
      </div>
    </div>
  );
}
